/**
 * Raw Material Transport Management System
 * Admin dashboard for managing transport routes, executions, and material collection
 * Complete workflow: Route Creation → Scheduling → Execution → Manufacturing Integration
 */

import { useEffect } from 'react';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { Truck, MapPin, Package, CheckCircle, Clock, AlertCircle } from 'lucide-react';
import { usePlatform } from '../contexts/PlatformContext';
import { useAuth } from '../contexts/AuthContext';
import BaseLayout, { NavigationItem, BreadcrumbItem } from '@/components/layout/BaseLayout';

// Import sub-pages
import TransportHome from './transport/TransportHome';
import RouteListPage from './transport/RouteListPage';
import CreateRoutePage from './transport/CreateRoutePage';
import RouteDetailsPage from './transport/RouteDetailsPage';
import ExecutionMonitorPage from './transport/ExecutionMonitorPage';
import LiveTrackingPage from './transport/LiveTrackingPage';

// Translations
interface LanguageContextType {
  language: 'hi' | 'en';
  setLanguage: (lang: 'hi' | 'en') => void;
  t: (key: string) => string;
}

const translations = {
  en: {
    title: 'Raw Material Transport',
    subtitle: 'Multi-gaushala collection routes and driver execution',
    home: 'Dashboard',
    routes: 'Routes',
    createRoute: 'Create Route',
    routeDetails: 'Route Details',
    executions: 'Executions',
    executionMonitor: 'Monitor Deliveries',
    liveTracking: 'Live Tracking',
    hindi: 'हिंदी',
    english: 'English'
  },
  hi: {
    title: 'कच्चा माल परिवहन',
    subtitle: 'बहु-गौशाला संग्रह मार्ग और चालक निष्पादन',
    home: 'डैशबोर्ड',
    routes: 'मार्ग',
    createRoute: 'मार्ग बनाएं',
    routeDetails: 'मार्ग विवरण',
    executions: 'निष्पादन',
    executionMonitor: 'डिलीवरी निगरानी',
    liveTracking: 'लाइव ट्रैकिंग',
    hindi: 'हिंदी',
    english: 'English'
  }
};

export default function Transport() {
  const navigate = useNavigate();
  const location = useLocation();
  const { platformSettings, updateBreadcrumbs } = usePlatform();
  const { user } = useAuth();

  const t = (key: string): string => {
    const keys = key.split('.');
    let value: any = translations[platformSettings.language];
    for (const k of keys) {
      value = value?.[k];
    }
    return value || key;
  };

  // Check role authorization
  useEffect(() => {
    if (user && !['ADMIN', 'TRANSPORT_ADMIN'].includes(user.role)) {
      navigate('/');
    }
  }, [user, navigate]);

  const navigationItems: NavigationItem[] = [
    {
      label: t('home'),
      icon: <Truck size={20} />,
      path: '/raw-material-transport',
      exact: true
    },
    {
      label: t('routes'),
      icon: <MapPin size={20} />,
      path: '/raw-material-transport/routes',
      exact: true
    },
    {
      label: t('createRoute'),
      icon: <Package size={20} />,
      path: '/raw-material-transport/routes/create',
      exact: true
    },
    {
      label: t('executionMonitor'),
      icon: <CheckCircle size={20} />,
      path: '/raw-material-transport/executions',
      exact: true
    },
    {
      label: t('liveTracking'),
      icon: <Clock size={20} />,
      path: '/raw-material-transport/live-tracking',
      exact: true
    }
  ];

  const getBreadcrumbs = (): BreadcrumbItem[] => {
    const pathSegments = location.pathname.split('/').filter(Boolean);
    const breadcrumbs: BreadcrumbItem[] = [
      { label: 'Home', path: '/' }
    ];

    if (pathSegments[0] === 'raw-material-transport') {
      breadcrumbs.push({ label: t('title'), path: '/raw-material-transport' });

      if (pathSegments[1] === 'routes') {
        breadcrumbs.push({ label: t('routes'), path: '/raw-material-transport/routes' });
        if (pathSegments[2] === 'create') {
          breadcrumbs.push({ label: t('createRoute') });
        } else if (pathSegments[2]) {
          breadcrumbs.push({ label: `${t('routeDetails')} #${pathSegments[2]}` });
        }
      } else if (pathSegments[1] === 'executions') {
        breadcrumbs.push({ label: t('executionMonitor') });
      } else if (pathSegments[1] === 'live-tracking') {
        breadcrumbs.push({ label: t('liveTracking') });
      }
    }

    return breadcrumbs;
  };

  return (
    <BaseLayout
      title={t('title')}
      subtitle={t('subtitle')}
      navigationItems={navigationItems}
      breadcrumbs={getBreadcrumbs()}
      onBreadcrumbClick={(path) => navigate(path)}
    >
      <Routes>
        <Route path="/" element={<TransportHome />} />
        <Route path="/routes" element={<RouteListPage />} />
        <Route path="/routes/create" element={<CreateRoutePage />} />
        <Route path="/routes/:routeId" element={<RouteDetailsPage />} />
        <Route path="/executions" element={<ExecutionMonitorPage />} />
        <Route path="/live-tracking" element={<LiveTrackingPage />} />
      </Routes>
    </BaseLayout>
  );
}
